import { useEffect, useState } from "react";

import useDebounce from "../../../../hooks/useDebounce";
import { InGameAttributeCategory } from "../../../../redux/squadBuilder/squadBuilderUtils";
import {
  BasicCustomFilterProps,
  RangeFilter,
} from "../../../molecules/Filters/RangeFilter";

type props = {
  title: InGameAttributeCategory;
  customFilters: BasicCustomFilterProps[];
  disabledInGameAttributes: boolean;
};

const DebouncedRangeFilter = ({
  label,
  onChangeMin,
  onChangeMax,
  minValue,
  maxValue,
  isTitle,
}: BasicCustomFilterProps & { isTitle: boolean }) => {
  // Local state for immediate updates
  const [localMinValue, setLocalMinValue] = useState(minValue);
  const [localMaxValue, setLocalMaxValue] = useState(maxValue);

  // Debounce the local values
  const debouncedMinValue = useDebounce(localMinValue, 300);
  const debouncedMaxValue = useDebounce(localMaxValue, 300);

  // Update local state when props change
  useEffect(() => {
    setLocalMinValue(minValue);
  }, [minValue]);

  useEffect(() => {
    setLocalMaxValue(maxValue);
  }, [maxValue]);

  // Call the handlers when debounced values change
  useEffect(() => {
    if (debouncedMinValue !== minValue) {
      onChangeMin(debouncedMinValue);
    }
  }, [debouncedMinValue, minValue, onChangeMin]);

  useEffect(() => {
    if (debouncedMaxValue !== maxValue) {
      onChangeMax(debouncedMaxValue);
    }
  }, [debouncedMaxValue, maxValue, onChangeMax]);

  return (
    <RangeFilter
      label={label}
      onChangeMin={setLocalMinValue}
      onChangeMax={setLocalMaxValue}
      minValue={localMinValue}
      maxValue={localMaxValue}
      isTitle={isTitle}
    />
  );
};

export const CustomFilterSection = ({
  title,
  customFilters,
  disabledInGameAttributes,
}: props) => {
  return (
    <div className="flex flex-col p-1 rounded-lg gap-y-0.5 bg-white/5">
      {customFilters.map((customFilter) => {
        return (
          (!disabledInGameAttributes || title === customFilter.label) && (
            <DebouncedRangeFilter
              key={customFilter.label}
              {...customFilter}
              isTitle={customFilter.label === title}
            />
          )
        );
      })}
    </div>
  );
};
